import React from "react";

import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormHelperText from "@mui/material/FormHelperText";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";
import Link from "@mui/material/Link";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import Switch from "@mui/material/Switch";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import NightsStayIcon from "@mui/icons-material/NightsStay";
import PeopleIcon from "@mui/icons-material/People";
import UploadIcon from "@mui/icons-material/Upload";

import {
  Partner,
  PARTNER_CAPABILITIES,
  PartnerCapability,
  PartnerLink,
  PartnerLinkPlatform,
  PartnerWelcomeNote,
  UpdatePartnerRequest,
  uploadPartnerAsset,
  WELCOME_NOTE_BODY_MAX,
  WELCOME_NOTE_TITLE_MAX,
} from "../../api/partners";
import { VideoPlatformResource } from "../../api/video_platform";
import { usePartnersStore } from "../../store/partnersStore";
import { useDialogState } from "../../hooks/useDialogState";
import { useIsMobile } from "../../hooks/useIsMobile";
import { useSnackbar } from "../../hooks/useSnackbar";
import { ColorField } from "../common/ColorField";
import { ConfirmActionDialog } from "../common/ConfirmActionDialog";
import { PartnerMembersDialog } from "./PartnerMembersDialog";

const LINK_PLATFORMS: PartnerLinkPlatform[] = [
  "Website",
  "Instagram",
  "Facebook",
  "Twitter",
  "LinkedIn",
  "YouTube",
  "TikTok",
  "Discourse",
  "Other",
];

interface PartnerDetailDialogProps {
  open: boolean;
  partner: Partner | undefined;
  onClose: () => void;
}

interface EditFields {
  displayName: string;
  aboutText: string;
  primaryColor: string;
  accentColor: string;
  discourseGroupName: string;
  discourseCategoryUrl: string;
  links: PartnerLink[];
  sendWelcomeEmail: boolean;
  sendOffboardingEmail: boolean;
  welcomeNoteTitle: string;
  welcomeNoteBody: string;
  capabilities: PartnerCapability[];
  portalEnabled: boolean;
}

const toEditFields = (partner: Partner): EditFields => ({
  displayName: partner.displayName,
  aboutText: partner.aboutText ?? "",
  primaryColor: partner.primaryColor ?? "",
  accentColor: partner.accentColor ?? "",
  discourseGroupName: partner.discourseGroupName ?? "",
  discourseCategoryUrl: partner.discourseCategoryUrl ?? "",
  links: partner.links ?? [],
  // Absent ⇒ send (matches the backend default).
  sendWelcomeEmail: partner.sendWelcomeEmail ?? true,
  sendOffboardingEmail: partner.sendOffboardingEmail ?? true,
  welcomeNoteTitle: partner.welcomeNote?.title ?? "",
  welcomeNoteBody: partner.welcomeNote?.body ?? "",
  capabilities: partner.capabilities ?? [],
  // Absent/null ⇒ un-migrated partner; default the toggle off until ops sets
  // capabilities and turns the portal on explicitly.
  portalEnabled: partner.portalEnabled ?? false,
});

const trimmedOrUndefined = (value: string) => {
  const trimmed = value.trim();
  return trimmed ? trimmed : undefined;
};

/**
 * Whether the edited capability list differs from what was loaded, with a
 * null/undefined original (never configured) compared as the same read-only
 * default the edit fields hydrate it to — so leaving the control untouched
 * never turns that null into an explicit [].
 */
const capabilitiesChanged = (
  original: PartnerCapability[] | null | undefined,
  next: PartnerCapability[],
): boolean => {
  const before = original ?? [];
  return before.length !== next.length || before.some((c) => !next.includes(c));
};

const portalEnabledChanged = (
  original: boolean | null | undefined,
  next: boolean,
): boolean => (original ?? false) !== next;

/**
 * The note is edited as two flat fields but stored as one object, so the pair
 * has to collapse back on save. Both blank clears the note; one blank is a
 * half-written note the API rejects, which surfaces as a save error rather
 * than being silently dropped here.
 */
const toWelcomeNotePayload = (
  title: string,
  body: string,
): PartnerWelcomeNote | null => {
  const trimmedTitle = title.trim();
  const trimmedBody = body.trim();
  if (!trimmedTitle && !trimmedBody) return null;
  return { title: trimmedTitle, body: trimmedBody };
};

/**
 * Render the note the way the member's dialog does — literal newlines as line
 * breaks and `**…**` as bold, nothing else — so ops can see what they wrote
 * before it ships. Deliberately not a markdown library: matching the client's
 * (equally minimal) renderer matters more than features, and anything richer
 * here would promise formatting the dialog won't honour.
 */
const WelcomeNotePreview: React.FC<{ title: string; body: string }> = ({
  title,
  body,
}) => (
  <Box>
    <Typography variant="subtitle2">{title}</Typography>
    {body.split("\n").map((line, i) => (
      <Typography key={i} variant="body2" sx={{ minHeight: "1.2em" }}>
        {line
          .split(/(\*\*[^*]+\*\*)/g)
          .map((part, j) =>
            part.startsWith("**") && part.endsWith("**") ? (
              <strong key={j}>{part.slice(2, -2)}</strong>
            ) : (
              part
            ),
          )}
      </Typography>
    ))}
  </Box>
);

const ColorSwatch: React.FC<{ label: string; color?: string }> = ({
  label,
  color,
}) => {
  if (!color) return null;
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Box
        sx={{
          width: 16,
          height: 16,
          borderRadius: 0.5,
          bgcolor: color,
          border: "1px solid rgba(0,0,0,0.2)",
        }}
      />
      <Typography variant="body2">
        {label}: {color}
      </Typography>
    </Box>
  );
};

export const PartnerDetailDialog: React.FC<PartnerDetailDialogProps> = ({
  open,
  partner,
  onClose,
}) => {
  const { partners, updatePartner } = usePartnersStore();
  const { showSnackbar, SnackbarNode } = useSnackbar();
  const membersDialog = useDialogState();
  const deactivateConfirm = useDialogState();
  const quietOwlConfirm = useDialogState();
  const isMobile = useIsMobile();

  const [editing, setEditing] = React.useState(false);
  const [fields, setFields] = React.useState<EditFields | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [statusLoading, setStatusLoading] = React.useState(false);
  const [provisioningRoom, setProvisioningRoom] = React.useState(false);
  const [uploading, setUploading] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // Always reflect latest store data so edits/uploads show immediately.
  const currentPartner = partners.find((p) => p.id === partner?.id) ?? partner;

  // Reset transient edit state whenever the dialog opens for a partner.
  React.useEffect(() => {
    if (open) {
      setEditing(false);
      setFields(null);
    }
  }, [open, partner?.id]);

  if (!currentPartner) return null;

  const isActive = currentPartner.status === "Active";

  const startEditing = () => {
    setFields(toEditFields(currentPartner));
    setEditing(true);
  };

  // The API rejects both of these with a 400, and the note rides in the same
  // payload as every other partner field — so an unsendable note would fail
  // the whole edit, colors and links included. Block the save here instead of
  // letting ops hit a server error they can already see coming.
  //
  // Lengths are measured trimmed, matching what the server actually caps: a
  // trailing newline on a full-length body should not block a save the API
  // would accept.
  const welcomeNoteInvalid =
    !!fields &&
    (fields.welcomeNoteTitle.trim().length > WELCOME_NOTE_TITLE_MAX ||
      fields.welcomeNoteBody.trim().length > WELCOME_NOTE_BODY_MAX ||
      // Exactly one side filled is a half-written note, not a cleared one.
      !fields.welcomeNoteTitle.trim() !== !fields.welcomeNoteBody.trim());

  const setField = <K extends keyof EditFields>(
    key: K,
    value: EditFields[K],
  ) => {
    setFields((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  const handleSave = async () => {
    if (!fields) return;
    setSaving(true);
    try {
      const original = toEditFields(currentPartner);
      const noteChanged =
        fields.welcomeNoteTitle.trim() !== original.welcomeNoteTitle.trim() ||
        fields.welcomeNoteBody.trim() !== original.welcomeNoteBody.trim();
      const payload: UpdatePartnerRequest = {
        displayName: fields.displayName.trim(),
        aboutText: trimmedOrUndefined(fields.aboutText),
        primaryColor: trimmedOrUndefined(fields.primaryColor),
        accentColor: trimmedOrUndefined(fields.accentColor),
        discourseGroupName: trimmedOrUndefined(fields.discourseGroupName),
        discourseCategoryUrl: trimmedOrUndefined(fields.discourseCategoryUrl),
        links: fields.links.filter((l) => l.url.trim()),
        // Every field below is omitted (rather than resent at its hydrated
        // default) unless the operator actually touched that control — each
        // has a documented "absent ⇒ leave unchanged" meaning on the
        // backend, and echoing the default back on every unrelated edit
        // would pin that meaning to an explicit value the operator never
        // chose, or clobber a concurrent edit to it.
        ...(fields.sendWelcomeEmail !== original.sendWelcomeEmail
          ? { sendWelcomeEmail: fields.sendWelcomeEmail }
          : {}),
        ...(fields.sendOffboardingEmail !== original.sendOffboardingEmail
          ? { sendOffboardingEmail: fields.sendOffboardingEmail }
          : {}),
        ...(noteChanged
          ? {
              welcomeNote: toWelcomeNotePayload(
                fields.welcomeNoteTitle,
                fields.welcomeNoteBody,
              ),
            }
          : {}),
        ...(capabilitiesChanged(
          currentPartner.capabilities,
          fields.capabilities,
        )
          ? { capabilities: fields.capabilities }
          : {}),
        ...(portalEnabledChanged(
          currentPartner.portalEnabled,
          fields.portalEnabled,
        )
          ? { portalEnabled: fields.portalEnabled }
          : {}),
      };
      await updatePartner(currentPartner.id, payload);
      showSnackbar("Partner updated.");
      setEditing(false);
    } catch (err) {
      showSnackbar(
        err instanceof Error ? err.message : "Failed to update partner.",
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async () => {
    setStatusLoading(true);
    try {
      await updatePartner(currentPartner.id, {
        status: isActive ? "Inactive" : "Active",
      });
      showSnackbar(isActive ? "Partner deactivated." : "Partner activated.");
    } catch (err) {
      showSnackbar(
        err instanceof Error ? err.message : "Failed to change status.",
        "error",
      );
    } finally {
      setStatusLoading(false);
      deactivateConfirm.close();
    }
  };

  // Provisions the partner's members-only "quiet owl" room: a persistent,
  // partner-scoped meeting. The backend gate + listing filters make it visible
  // and joinable only to this partner's members (plus Admins/Hosts).
  const handleProvisionQuietOwl = async () => {
    setProvisioningRoom(true);
    try {
      await VideoPlatformResource.createMeeting({
        title: `${currentPartner.displayName} — Quiet Owl Room`,
        type: "Persistent",
        category: "quiet_owl",
        partnerId: currentPartner.id,
        // partnerId governs visibility; isPublicVisible is left false so the
        // room is never publicly listed even if the partner scope were cleared.
        isPublicVisible: false,
      });
      showSnackbar("Quiet owl room created for this partner.");
    } catch (err) {
      showSnackbar(
        err instanceof Error ? err.message : "Failed to create quiet owl room.",
        "error",
      );
    } finally {
      setProvisioningRoom(false);
      quietOwlConfirm.close();
    }
  };

  const handleLogoFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file
    if (!file) return;
    setUploading(true);
    try {
      const logoUrl = await uploadPartnerAsset(currentPartner.id, file);
      await updatePartner(currentPartner.id, { logoUrl });
      showSnackbar("Logo updated.");
    } catch (err) {
      showSnackbar(
        err instanceof Error ? err.message : "Failed to upload logo.",
        "error",
      );
    } finally {
      setUploading(false);
    }
  };

  const addLink = () =>
    setField("links", [
      ...(fields?.links ?? []),
      { platform: "Website", url: "" },
    ]);

  const updateLink = (index: number, patch: Partial<PartnerLink>) =>
    setField(
      "links",
      (fields?.links ?? []).map((l, i) =>
        i === index ? { ...l, ...patch } : l,
      ),
    );

  const removeLink = (index: number) =>
    setField(
      "links",
      (fields?.links ?? []).filter((_, i) => i !== index),
    );

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1.5,
              minWidth: 0,
            }}
          >
            <Avatar
              src={currentPartner.logoUrl}
              alt={currentPartner.displayName}
              sx={{ bgcolor: currentPartner.primaryColor }}
            >
              {currentPartner.displayName.charAt(0).toUpperCase()}
            </Avatar>
            <Typography
              variant="h6"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {currentPartner.displayName}
            </Typography>
            <Chip label={`#${currentPartner.hashtag}`} size="small" />
            <Chip
              label={currentPartner.status}
              size="small"
              color={isActive ? "success" : "default"}
            />
          </Box>
          <IconButton size="small" onClick={onClose} sx={{ flexShrink: 0 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {editing && fields ? (
            <Stack spacing={2}>
              <TextField
                fullWidth
                label="Display Name"
                value={fields.displayName}
                onChange={(e) => setField("displayName", e.target.value)}
              />
              <TextField
                fullWidth
                label="About"
                multiline
                minRows={2}
                value={fields.aboutText}
                onChange={(e) => setField("aboutText", e.target.value)}
              />
              <Divider textAlign="left">
                <Typography variant="overline">Welcome note</Typography>
              </Divider>
              <Typography variant="caption" color="text.secondary">
                Shown once, the first time a member arrives in a video session.
                Leave both fields blank to remove it.
              </Typography>
              <TextField
                fullWidth
                label="Welcome Note Title"
                value={fields.welcomeNoteTitle}
                onChange={(e) => setField("welcomeNoteTitle", e.target.value)}
                error={
                  fields.welcomeNoteTitle.trim().length >
                    WELCOME_NOTE_TITLE_MAX ||
                  (!fields.welcomeNoteTitle.trim() &&
                    !!fields.welcomeNoteBody.trim())
                }
                helperText={
                  !fields.welcomeNoteTitle.trim() &&
                  fields.welcomeNoteBody.trim()
                    ? "Required when the body is filled in"
                    : `${fields.welcomeNoteTitle.trim().length}/${WELCOME_NOTE_TITLE_MAX}`
                }
              />
              <TextField
                fullWidth
                label="Welcome Note Body"
                multiline
                minRows={6}
                value={fields.welcomeNoteBody}
                onChange={(e) => setField("welcomeNoteBody", e.target.value)}
                error={
                  fields.welcomeNoteBody.trim().length >
                    WELCOME_NOTE_BODY_MAX ||
                  (!fields.welcomeNoteBody.trim() &&
                    !!fields.welcomeNoteTitle.trim())
                }
                helperText={
                  // Not a hard maxLength: truncating a partner's copy mid-word
                  // on paste is worse than showing them what needs cutting.
                  !fields.welcomeNoteBody.trim() &&
                  fields.welcomeNoteTitle.trim()
                    ? "Required when the title is filled in"
                    : `${fields.welcomeNoteBody.trim().length}/${WELCOME_NOTE_BODY_MAX} · blank line between paragraphs, **bold** for emphasis`
                }
              />
              {(fields.welcomeNoteTitle.trim() ||
                fields.welcomeNoteBody.trim()) && (
                <Box sx={{ p: 2, bgcolor: "action.hover", borderRadius: 1 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    gutterBottom
                  >
                    Preview
                  </Typography>
                  <WelcomeNotePreview
                    title={fields.welcomeNoteTitle}
                    body={fields.welcomeNoteBody}
                  />
                </Box>
              )}
              <Divider />
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: 2,
                }}
              >
                <ColorField
                  fullWidth
                  label="Primary Color"
                  placeholder="#1A2B3C"
                  value={fields.primaryColor}
                  onChange={(color) => setField("primaryColor", color)}
                />
                <ColorField
                  fullWidth
                  label="Accent Color"
                  placeholder="#4D5E6F"
                  value={fields.accentColor}
                  onChange={(color) => setField("accentColor", color)}
                />
              </Box>
              <TextField
                fullWidth
                label="Discourse Group Name"
                value={fields.discourseGroupName}
                onChange={(e) => setField("discourseGroupName", e.target.value)}
              />
              <TextField
                fullWidth
                label="Discourse Category URL"
                value={fields.discourseCategoryUrl}
                onChange={(e) =>
                  setField("discourseCategoryUrl", e.target.value)
                }
              />

              <Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography variant="subtitle2" color="text.secondary">
                    Links
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={addLink}
                  >
                    Add Link
                  </Button>
                </Box>
                <Stack spacing={1}>
                  {fields.links.map((link, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 1,
                        alignItems: "flex-start",
                      }}
                    >
                      <TextField
                        select
                        size="small"
                        label="Platform"
                        value={link.platform}
                        onChange={(e) =>
                          updateLink(index, {
                            platform: e.target.value as PartnerLinkPlatform,
                          })
                        }
                        sx={{ minWidth: 130 }}
                      >
                        {LINK_PLATFORMS.map((p) => (
                          <MenuItem key={p} value={p}>
                            {p}
                          </MenuItem>
                        ))}
                      </TextField>
                      <TextField
                        size="small"
                        label="URL"
                        value={link.url}
                        onChange={(e) =>
                          updateLink(index, { url: e.target.value })
                        }
                        sx={{ flex: "1 1 200px" }}
                      />
                      <TextField
                        size="small"
                        label="Label"
                        value={link.label ?? ""}
                        onChange={(e) =>
                          updateLink(index, { label: e.target.value })
                        }
                        sx={{ minWidth: 120 }}
                      />
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => removeLink(index)}
                        sx={{ mt: 0.5 }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Stack>
              </Box>

              <Box>
                <Divider sx={{ mb: 1 }} />
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={{ mb: 0.5 }}
                >
                  Transactional emails
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={fields.sendWelcomeEmail}
                      onChange={(e) =>
                        setField("sendWelcomeEmail", e.target.checked)
                      }
                    />
                  }
                  label="Send welcome email on provisioning"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={fields.sendOffboardingEmail}
                      onChange={(e) =>
                        setField("sendOffboardingEmail", e.target.checked)
                      }
                    />
                  }
                  label="Send offboarding email on removal"
                />
              </Box>

              <Box>
                <Divider sx={{ mb: 1 }} />
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={{ mb: 0.5 }}
                >
                  Partner Portal
                </Typography>
                <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                  <InputLabel id="partner-capabilities-label" shrink>
                    Member management
                  </InputLabel>
                  <Select
                    labelId="partner-capabilities-label"
                    label="Member management"
                    notched
                    displayEmpty
                    multiple
                    value={fields.capabilities}
                    onChange={(e: SelectChangeEvent<PartnerCapability[]>) => {
                      const value = e.target.value;
                      setField(
                        "capabilities",
                        typeof value === "string"
                          ? (value.split(",") as PartnerCapability[])
                          : value,
                      );
                    }}
                    renderValue={(selected) =>
                      selected.length
                        ? selected
                            .map(
                              (v) =>
                                PARTNER_CAPABILITIES.find((c) => c.value === v)
                                  ?.label ?? v,
                            )
                            .join(", ")
                        : "Read-only roster"
                    }
                  >
                    {PARTNER_CAPABILITIES.map((cap) => (
                      <MenuItem key={cap.value} value={cap.value}>
                        <Checkbox
                          size="small"
                          checked={fields.capabilities.includes(cap.value)}
                        />
                        <ListItemText
                          primary={cap.label}
                          secondary={cap.help}
                        />
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    Empty selection = read-only roster (checkout-managed).
                  </FormHelperText>
                </FormControl>
                <FormControlLabel
                  control={
                    <Switch
                      checked={fields.portalEnabled}
                      onChange={(e) =>
                        setField("portalEnabled", e.target.checked)
                      }
                    />
                  }
                  label="Portal enabled"
                />
                <FormHelperText sx={{ mt: -0.5 }}>
                  Kill switch for this partner's portal/API access, independent
                  of member management above.
                </FormHelperText>
              </Box>
            </Stack>
          ) : (
            <Stack spacing={2.5}>
              {currentPartner.aboutText && (
                <Box>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ mb: 0.5 }}
                  >
                    About
                  </Typography>
                  <Typography variant="body2">
                    {currentPartner.aboutText}
                  </Typography>
                </Box>
              )}

              {currentPartner.welcomeNote && (
                <Box>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ mb: 0.5 }}
                  >
                    Welcome Note
                  </Typography>
                  <WelcomeNotePreview
                    title={currentPartner.welcomeNote.title}
                    body={currentPartner.welcomeNote.body}
                  />
                </Box>
              )}

              <Box>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={{ mb: 0.5 }}
                >
                  Backing Group
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                  {currentPartner.groupId}
                </Typography>
              </Box>

              {(currentPartner.primaryColor || currentPartner.accentColor) && (
                <Box>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ mb: 0.5 }}
                  >
                    Branding
                  </Typography>
                  <Stack spacing={0.5}>
                    <ColorSwatch
                      label="Primary"
                      color={currentPartner.primaryColor}
                    />
                    <ColorSwatch
                      label="Accent"
                      color={currentPartner.accentColor}
                    />
                  </Stack>
                </Box>
              )}

              {(currentPartner.discourseGroupName ||
                currentPartner.discourseCategoryUrl) && (
                <Box>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ mb: 0.5 }}
                  >
                    Discourse
                  </Typography>
                  {currentPartner.discourseGroupName && (
                    <Typography variant="body2">
                      Group: {currentPartner.discourseGroupName}
                    </Typography>
                  )}
                  {currentPartner.discourseCategoryUrl && (
                    <Link
                      href={currentPartner.discourseCategoryUrl}
                      target="_blank"
                      rel="noopener"
                      variant="body2"
                      sx={{ wordBreak: "break-all" }}
                    >
                      {currentPartner.discourseCategoryUrl}
                    </Link>
                  )}
                </Box>
              )}

              <Box>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={{ mb: 0.5 }}
                >
                  Links ({currentPartner.links?.length ?? 0})
                </Typography>
                {currentPartner.links && currentPartner.links.length > 0 ? (
                  <Stack spacing={0.5}>
                    {currentPartner.links.map((link, index) => (
                      <Box
                        key={index}
                        sx={{ display: "flex", gap: 1, alignItems: "center" }}
                      >
                        <Chip
                          label={link.platform}
                          size="small"
                          variant="outlined"
                        />
                        <Link
                          href={link.url}
                          target="_blank"
                          rel="noopener"
                          variant="body2"
                          sx={{ wordBreak: "break-all" }}
                        >
                          {link.label || link.url}
                        </Link>
                      </Box>
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.disabled">
                    No links.
                  </Typography>
                )}
              </Box>

              <Box>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={{ mb: 0.5 }}
                >
                  Transactional emails
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Chip
                    label={`Welcome: ${currentPartner.sendWelcomeEmail === false ? "Off" : "On"}`}
                    size="small"
                    color={
                      currentPartner.sendWelcomeEmail === false
                        ? "default"
                        : "success"
                    }
                    variant="outlined"
                  />
                  <Chip
                    label={`Offboarding: ${currentPartner.sendOffboardingEmail === false ? "Off" : "On"}`}
                    size="small"
                    color={
                      currentPartner.sendOffboardingEmail === false
                        ? "default"
                        : "success"
                    }
                    variant="outlined"
                  />
                </Stack>
              </Box>

              <Box>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={{ mb: 0.5 }}
                >
                  Partner Portal
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {currentPartner.capabilities &&
                  currentPartner.capabilities.length > 0 ? (
                    currentPartner.capabilities.map((cap) => (
                      <Chip
                        key={cap}
                        label={
                          PARTNER_CAPABILITIES.find((c) => c.value === cap)
                            ?.label ?? cap
                        }
                        size="small"
                        color="success"
                        variant="outlined"
                      />
                    ))
                  ) : (
                    <Chip
                      label="Read-only roster"
                      size="small"
                      variant="outlined"
                    />
                  )}
                  <Chip
                    label={`Portal: ${currentPartner.portalEnabled ? "Enabled" : "Disabled"}`}
                    size="small"
                    color={currentPartner.portalEnabled ? "success" : "default"}
                    variant="outlined"
                  />
                </Stack>
              </Box>

              <Box>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  Logo
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={
                    uploading ? <CircularProgress size={16} /> : <UploadIcon />
                  }
                  disabled={uploading}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {uploading ? "Uploading..." : "Upload Logo"}
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/svg+xml"
                  hidden
                  onChange={handleLogoFile}
                />
              </Box>
            </Stack>
          )}
        </DialogContent>

        <DialogActions
          sx={{ justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}
        >
          {editing ? (
            <>
              <Button onClick={() => setEditing(false)} disabled={saving}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving || welcomeNoteInvalid}
              >
                {saving ? "Saving..." : "Save Changes"}
              </Button>
            </>
          ) : (
            <>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                <Button startIcon={<EditIcon />} onClick={startEditing}>
                  Edit Details
                </Button>
                <Button
                  startIcon={<PeopleIcon />}
                  onClick={membersDialog.openDialog}
                >
                  Members
                </Button>
                <Button
                  startIcon={<NightsStayIcon />}
                  onClick={quietOwlConfirm.openDialog}
                >
                  Quiet Owl Room
                </Button>
                <Button
                  color={isActive ? "error" : "success"}
                  variant="outlined"
                  onClick={
                    isActive ? deactivateConfirm.openDialog : handleToggleStatus
                  }
                  disabled={statusLoading}
                >
                  {isActive ? "Deactivate" : "Activate"}
                </Button>
              </Box>
              <Button onClick={onClose}>Close</Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <PartnerMembersDialog
        open={membersDialog.open}
        partnerId={currentPartner.id}
        partnerName={currentPartner.displayName}
        onClose={membersDialog.close}
      />

      <ConfirmActionDialog
        open={deactivateConfirm.open}
        title="Deactivate partner?"
        message={
          <>
            <strong>{currentPartner.displayName}</strong> will be marked
            Inactive. Its members keep their accounts, but the partner stops
            being offered as active across the platform.
          </>
        }
        confirmLabel="Deactivate partner"
        confirmText={currentPartner.hashtag}
        loading={statusLoading}
        onClose={deactivateConfirm.close}
        onConfirm={handleToggleStatus}
      />

      <ConfirmActionDialog
        open={quietOwlConfirm.open}
        title="Create quiet owl room?"
        message={
          <>
            Creates a members-only "quiet owl" video room for{" "}
            <strong>{currentPartner.displayName}</strong>. Only this partner's
            members (plus Admins/Hosts) will see and be able to join it. Running
            this again creates another room, so only do it if one doesn't exist
            yet.
          </>
        }
        confirmLabel="Create room"
        loading={provisioningRoom}
        onClose={quietOwlConfirm.close}
        onConfirm={handleProvisionQuietOwl}
      />

      {SnackbarNode}
    </>
  );
};
